<template>
  <GcBaseLayout
    :title="'Production Schedule'"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="false"
    :backButtonFunction="undefined"
    :formsConfig="formsConfig"
    :tabs="getBranchTabs"
    :selectedIndex="selectedTabIndex"
    ref="gc-base-layout"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Production Schedule'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="'Production Schedule'"
        :secondaryLabel="undefined"
      />
    </template>

    <div class="gc-syncfusion production-calendar">
      <div class="main-content" v-if="loaded">
        <div>
          <DxTabs
            :selected-index="SelectedProcessTab"
            :items="getProcessTabs"
            :orientation="'horizontal'"
            :styling-mode="'secondary'"
            :width="'auto'"
            :keyExpr="'id'"
            @itemClick="onProcessChanged"
          />
        </div>
        <gc-filter-split-view
          :showLeftContainer="showLeftContainer"
          :showRightContainer="showRightContainer"
          :rightContainerWidth="250"
        >
          <template #leftContent>
            <div>
              <DxTabs
                :selected-index="SelectedScheduleTab"
                :items="[
                  {
                    id: 0,
                    text: 'Work Orders',
                  },
                  {
                    id: 1,
                    text: 'Linked Sales Orders',
                  },
                ]"
                :orientation="'horizontal'"
                :styling-mode="'secondary'"
                :width="'auto'"
                :keyExpr="'id'"
                @itemClick="onScheduleTabChanged"
              />
            </div>
            <div class="button-toolbar">
              <div>
                <DxButton
                  text="Add New"
                  type="default"
                  styling-mode="contained"
                  class="button-full-width margin-right-5 override-styles"
                  icon="plus"
                  width="75"
                  :visible="getPermissionToCreate && SelectedScheduleTab === 0"
                  @click="addNewWorkOrder()"
                />
              </div>
              <div>
                <DxButton
                  type="normal"
                  styling-mode="contained"
                  class="button-full-width override-styles"
                  icon="filter"
                  width="30"
                  v-if="!showRightContainer"
                  @click="openFilterPanel()"
                />
              </div>
            </div>
            <ejs-schedule
              :id="'ejs-schedule'"
              :height="height"
              :width="width"
              :views="views"
              :showQuickInfo="isTablet"
              :selectedDate="selectedDate"
              :eventSettings="getEventSettings"
              :popupOpen="onPopupOpen"
              :eventClick="onEventClick"
              :dragStart="onDragStart"
              :dragStop="onDragStop"
              ref="ejs-schedule-ref"
              :timeScale="{
                enable: false,
              }"
              :eventRendered="onEventRendered"
              :allowDragAndDrop="currentView !== 'Day'"
              @navigating="onNavigating"
            >
              <e-views>
                <!-- Day View Tiles -->
                <e-view
                  option="Day"
                  dateFormat="dd-MMM-yyyy"
                  :eventTemplate="'dayEventTemplate'"
                >
                  <template v-slot:dayEventTemplate="{ data }">
                    <CalendarDayTile
                      :data="data"
                      v-if="SelectedScheduleTab === 0"
                    />

                    <SODayTile :data="data" v-else />
                  </template>
                </e-view>

                <!-- Week View Tile -->
                <e-view
                  option="Week"
                  dateFormat="dd-MMM-yyyy"
                  :eventTemplate="'weekEventTemplate'"
                >
                  <template v-slot:weekEventTemplate="{ data }">
                    <CalendarWeekTile
                      :data="data"
                      v-if="SelectedScheduleTab === 0"
                    />
                    <SOWeekTile :data="data" v-else />
                  </template>
                </e-view>

                <!-- Work Week View Tile -->
                <e-view
                  option="WorkWeek"
                  dateFormat="dd-MMM-yyyy"
                  :eventTemplate="'workEventTemplate'"
                >
                  <template v-slot:workEventTemplate="{ data }">
                    <CalendarWeekTile
                      :data="data"
                      v-if="SelectedScheduleTab === 0"
                    />
                    <SOWeekTile :data="data" v-else />
                  </template>
                </e-view>

                <!-- Month View Tile -->
                <e-view option="Month" :eventTemplate="'monthEventTemplate'">
                  <template v-slot:monthEventTemplate="{ data }">
                    <CalendarMonthTile
                      :data="data"
                      v-if="SelectedScheduleTab === 0"
                    />
                    <SOMonthTile :data="data" v-else />
                  </template>
                </e-view>
              </e-views>
              <!-- main tile template  -->

              <!-- tooltip template  -->
              <template v-slot:tooltipTemplate="{ data }">
                <WOToolTip :data="data" v-if="SelectedScheduleTab === 0" />
                <SOToolTip :data="data" v-else />
              </template>
            </ejs-schedule>
          </template>
          <template #rightContent>
            <gc-filter-panel
              :toolbarVisible="true"
              :toolbar="filterToolbar"
              :formItemData="filterFormItems"
              :setGridPanelFilter="setGridPanelFilter"
              :gridFoolter="false"
              :showLoading="showLoading"
              :from="'transaction-screen'"
              :isNormal="false"
              :persistenceKey="undefined"
              :key="'fp-call-schedule'"
              :overWriteClear="true"
              :ref="'gc-filter-panel'"
              :heightAdjustment="36"
            >
            </gc-filter-panel>
          </template>
        </gc-filter-split-view>
      </div>

      <GcPopUpWorkOrder
        :popupConfig="popupConfig"
        :on-hide="onPopupHide"
        :wrapper-attr="{
          class: 'css-v2 gc-popup gc-popup-form gc-popup-work-order',
        }"
        :data-model="dataModel"
        :show-loading="showLoading"
        :width="1024"
        :height="370"
        :save-call-back="onWorkOrderSave"
        :displayServerError="displayServerError"
        v-if="popupConfig.isPopupShow"
      />
    </div>
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import {
  Day,
  Week,
  WorkWeek,
  Month,
  Agenda,
  DragAndDrop,
  ViewDirective,
  ViewsDirective,
} from "@syncfusion/ej2-vue-schedule";

import { ProductionCalendarService } from "../../_services/api/production/production-calendar.service";
import { ProductionCalendarLinkedSOService } from "../../_services/api/production/production-calendar-linked-so";
import { getCalendarFilterValues } from "../../_helpers/calendar/filter-panel";

import { DataManager, ODataV4Adaptor } from "@syncfusion/ej2-data";
import { FeatureStorage, TokenStorage } from "../../_services/storage";
import appConfig from "../../app-config";
import { WorkOrderService } from "@/_services/api/production/work-order.service";
import { formatNumber } from "devextreme/localization";
import CalendarDayTile from "../../components/production/work-order/calendar-day-tile.vue";
import CalendarWeekTile from "../../components/production/work-order/calendar-week-tile.vue";
import CalendarMonthTile from "../../components/production/work-order/calendar-month-tile.vue";
import WOToolTip from "../../components/production/work-order/wo-tool-tip.vue";

// Tiles for Linked SOs
import SODayTile from "../../components/production/linked-sales-orders/so-day-tile.vue";
import SOWeekTile from "../../components/production/linked-sales-orders/so-week-tile.vue";
import SOMonthTile from "../../components/production/linked-sales-orders/so-month-tile.vue";
import SOToolTip from "../../components/production/linked-sales-orders/so-tool-tip.vue";
import { BranchService } from "../../_services/api/sales/branch.service";

function getRandomHexColor() {
  const randomColor = Math.floor(Math.random() * 0xffffff).toString(16);
  return `#${randomColor.padStart(6, "0")}`;
}

class WorkOrderAdaptor extends ODataV4Adaptor {
  processResponse() {
    let original = super.processResponse.apply(this, arguments);

    original.forEach((item) => {
      item.subject = item.WorkOrderCode;
      item.startTime = new Date(item.ScheduledDate);
      item.endTime = new Date(item.ScheduledDate);
      item.IsAllDay = true;
      item.backGround = getRandomHexColor();
    });

    return original;
  }
}
class LinkSOAdaptor extends ODataV4Adaptor {
  processResponse() {
    let original = super.processResponse.apply(this, arguments);

    original.forEach((item, index) => {
      // Convert to a fixed-time string, preserving the original timezone
      let dateObj = new Date(item.SalesOrderShipDate);
      item.SalesOrderShipDate = dateObj.toISOString().replace("Z", "");

      item.id = index + 1;
      item.subject = item.WorkOrderCode;
      item.startTime = new Date(item.PackDate).toFilterDateStartTime();
      item.endTime = new Date(item.PackDate).toFilterDateEndTime();
      item.IsAllDay = true;
      item.backGround = getRandomHexColor();

      if (!item.TransportStatusHexColour)
        item.TransportStatusHexColour = "#800080";
    });

    return original;
  }
}

let draggedEvent = null;
// let draggedElement = null;
let refreshLayout = null;

export default {
  mixins: [RouteMixin, FormMixin],
  provide: {
    schedule: [Day, Week, WorkWeek, Month, Agenda, DragAndDrop],
  },
  components: {
    "e-views": ViewsDirective,
    "e-view": ViewDirective,
    CalendarDayTile,
    CalendarWeekTile,
    CalendarMonthTile,
    WOToolTip,
    SODayTile,
    SOWeekTile,
    SOMonthTile,
    SOToolTip,
  },
  data() {
    return {
      tabInfo: [],
      selectedIndex: 0,
      showLeftContainer: true,
      showRightContainer: true,
      loaded: false,
      height: "auto",
      width: "100%",
      views: ["Day", "Week", "WorkWeek", "Month"],
      selectedDate: new Date(),
      filterToolbar: {
        close: {
          icon: "showpanel",
          onClick: () => {
            this.showRightContainer = !this.showRightContainer;
            this.$nextTick(() => {
              this.rePaintSchedule();
            });
          },
        },
      },
      filterValues: false,
      ProcessCode: undefined,
      popupConfig: {
        title: "",
        secondaryTitle: undefined,
        isFormLoading: false,
        popupKey: 0,
        formKey: 0,
        height: 370,
        isPopupShow: false,
        hideOnOutsideClick: false,
        toolbarMenuItems: [
          {
            menuText: "Copy",
            visible: false, // pending discussions #54331
          },
          { menuText: "Cancel", onClick: this.onCancelWorkOrderHandler },
          { menuText: "Reopen", onClick: this.onReopenWorkOrderHandler },
          { menuText: "Complete", onClick: this.onCompleteWorkOrderHandler },
        ],
        formMode: "create",
        isEditable: false,
        calendarDate: undefined,
        workOrderCode: undefined,
      },
      dataModel: undefined,
      SelectedScheduleTab: 0,
      SelectedProcessTab: 0,
      currentView: undefined,
      branchTabs: [],
      processTabs: [],
      BranchCode: undefined,
    };
  },
  async beforeCreate() {
    await BranchService.getManufactureBranch().then((res) => {
      res.data.value.forEach((element) => {
        this.branchTabs.push({
          BranchCode: element.Code,
          text: element.Code + " - " + element.Description,
          path: `/production-calendar/` + element.Code,
          applicationFeatureCode: "ProductionSchedule",
          onClick: this.onBranchChanged,
        });
      });
    });

    await ProductionCalendarService.getProductionProcess().then((res) => {
      res.data.value.forEach((element, index) => {
        this.processTabs.push({
          id: index,
          code: element.Code,
          text: element.Description,
          applicationFeatureCode: "ProductionSchedule",
          onClick: this.onProcessChanged,
        });
      });
    });

    this.ProcessCode = this.processTabs[0].code;

    if (this.baseView.id) {
      let index = this.branchTabs.findIndex(
        (x) => x.BranchCode === this.baseView.id
      );
      if (index !== -1) this.selectedIndex = index;
      this.BranchCode = this.baseView.id;
    } else {
      this.BranchCode = this.branchTabs[0].BranchCode;
    }
  },
  mounted() {
    setTimeout(() => {
      var scrollableView = document.getElementsByClassName(
        "layout-body dx-scrollview"
      );

      this.height = scrollableView[0].clientHeight - 65 - 74;
    }, 250);
  },
  async created() {
    this.loaded = true;
  },
  methods: {
    onScheduleTabChanged(e) {
      // Work Orders
      if (e.itemIndex === 0) {
        this.SelectedScheduleTab = 0;
        this.$nextTick(() => {
          this.rePaintSchedule();
        });
      }
      // Linked Sales Orders
      else if (e.itemIndex === 1) {
        this.SelectedScheduleTab = 1;
        this.$nextTick(() => {
          this.rePaintSchedule();
        });
      }
    },
    rePaintSchedule() {
      let scheduleObj =
        document.getElementById("ejs-schedule").ej2_instances[0];

      scheduleObj.refresh();
    },
    getFormattedQty(value) {
      return formatNumber(value, "##,###,###,###,##0.00");
    },
    /**
     * Open Filter Panel
     */
    openFilterPanel() {
      this.showRightContainer = true;
      this.$nextTick(() => {
        this.rePaintSchedule();
      });
    },
    /**
     * Filter Panel values
     * @param e
     * @param valueChanged
     */
    setGridPanelFilter(e, valueChanged) {
      this.filterValues = getCalendarFilterValues(e, valueChanged);
    },
    onBranchChanged(e) {
      if (e && e.BranchCode !== undefined && this.BranchCode !== e.BranchCode) {
        this.showLoading(true);
        let scheduleObj =
          document.getElementById("ejs-schedule").ej2_instances[0];

        if (scheduleObj) {
          this.filterValues = undefined;
          this.$refs["gc-filter-panel"].$refs["gc-form"].clearFormModel();
          //   scheduleObj.refresh();
          this.$nextTick(() => {
            this.BranchCode = e.BranchCode;
            setTimeout(() => {
              this.showLoading(false);
            }, 500);
          });
        } else this.showLoading(false);
      }
    },
    /**
     * dynamic tab value changed
     */
    onProcessChanged(e) {
      let item = e.itemData;
      if (item && item.code !== undefined && this.ProcessCode !== item.code) {
        this.showLoading(true);
        let scheduleObj =
          document.getElementById("ejs-schedule").ej2_instances[0];

        if (scheduleObj) {
          this.filterValues = undefined;
          this.$refs["gc-filter-panel"].$refs["gc-form"].clearFormModel();
          //   scheduleObj.refresh();
          this.$nextTick(() => {
            this.ProcessCode = item.code;
            setTimeout(() => {
              this.showLoading(false);
            }, 500);
          });
        } else this.showLoading(false);
      }
    },

    /**
     * on default popup open event
     * @param args
     */
    async onPopupOpen(args) {
      if (args.type === "Editor" && this.SelectedScheduleTab === 0) {
        args.cancel = true;

        this.popupConfig.title = "Create Work Order";
        this.popupConfig.hideOnOutsideClick = false;
        this.popupConfig.workOrderCode = undefined;
        this.popupConfig.formMode = "create";
        await this.showEventCreateOrEditModal(args, args.data);
      }

      if (args.type === "Editor" && this.SelectedScheduleTab === 1) {
        args.cancel = true;
      }
    },

    /**
     * on event click event - by default event
     * @param args
     */
    async onEventClick(args) {
      if (args.name === "eventClick") {
        args.cancel = true;
        this.showLoading(true);

        let data = (this.dataModel = args.event);
        if (this.SelectedScheduleTab === 1) {
          data.FriendlyStatus = data.WorkOrderFriendlyStatus;
        }

        this.popupConfig.formMode = "review";
        this.popupConfig.hideOnOutsideClick = true;
        this.popupConfig.workOrderCode = data.WorkOrderCode;

        this.popupConfig.title = `Work Order #${data.WorkOrderCode}`;
        this.popupConfig.secondaryTitle =
          data.ProductCode + " - " + data.ProductDescription1;
        this.popupConfig.isEditable =
          (data.FriendlyStatus === "Active" ||
            data.FriendlyStatus === "In Production") &&
          FeatureStorage.checkPermission("WorkOrder", "edit");

        const response = await WorkOrderService.getByCode(
          this.popupConfig.workOrderCode
        );
        this.dataModel = response.data.value[0];
        this.popupConfig.isPopupShow = true;

        // await this.showEventCreateOrEditModal(args, args.event);
      }
    },

    /**
     * on drag starts event
     */
    onDragStart(e, eventData) {
      if (this.currentView === "Day" && e.e.srcElement.dataset.tooltipId)
        document.getElementById(
          e.e.srcElement.dataset.tooltipId
        ).style.display = "none";

      if (
        this.getPermissionToCreate == false ||
        (this.SelectedScheduleTab === 0 && e.data.FriendlyStatus !== "Active")
      )
        e.cancel = true;

      if (
        this.SelectedScheduleTab === 1 &&
        this.getPermissionToCreate == false &&
        (e.data.FriendlyStatus !== "Active" ||
          e.data.FriendlyStatus !== "In Production")
      )
        e.cancel = true;

      draggedEvent = eventData;
    },

    /**
     * on drag stop event - this function get triggers in week, workweek and month view.
     * between diff dates
     * @param {Object} e - The event object associated with the drag stop.
     */
    async onDragStop(e) {
      e.cancel = true;
      const isSameDate = (date1, date2) => {
        return (
          date1.getFullYear() === date2.getFullYear() &&
          date1.getMonth() === date2.getMonth() &&
          date1.getDate() === date2.getDate()
        );
      };

      if (isSameDate(e.data.PackDate, e.data.startTime)) {
        return;
      }

      if (e.data) {
        // this.showLoading(true);
        var model = e.data;
        // // update single event
        // let payload = {
        //   ProcessCode: this.ProcessCode,
        //   ScheduledDate: new Date(model.startTime).toSaveDate(),
        //   WorkOrderCode: model.WorkOrderCode,
        //   Comments: model.Comments,
        // };

        // await WorkOrderService.workOrderReschedule(payload)
        //   .then((res) => {
        //     if (res) this.showLoading(false);
        //     this.$notify(
        //       `Work order #${model.WorkOrderCode} is successfully updated.`,
        //       "success"
        //     );
        //     this.onWorkOrderSave();
        //   })
        //   .catch((err) => {
        //     if (err) this.showLoading(false);
        //     this.displayServerError(err);
        //   });

        if (this.SelectedScheduleTab === 0)
          this.reOrderWorkOrder({
            ProcessCode: this.ProcessCode,
            OldScheduledDate: new Date(model.ScheduledDate).toSaveDate(),
            NewScheduledDate: new Date(model.startTime).toSaveDate(),
            WorkOrderCode: model.WorkOrderCode,
            NewPosition: -1,
          });
        else {
          this.reOrderLinkSoLines({
            ProcessCode: this.ProcessCode,
            SalesOrderCode: model.SalesOrderCode,
            SalesOrderLine: model.SalesOrderLine,
            OldScheduledDate: new Date(model.PackDate).toSaveDate(),
            NewScheduledDate: new Date(model.startTime).toSaveDate(),
            WorkOrderCode: model.WorkOrderCode,
            NewPosition: -1,
          });
        }
      }
    },

    onEventRendered(args) {
      args.element.style.height = "auto";
      setTimeout(() => {
        if (args.element.previousElementSibling) {
          args.element.style.top = `${
            args.element.previousElementSibling.offsetHeight +
            args.element.previousElementSibling.offsetTop +
            5
          }px`;
        }

        if (this.currentView === "Day") {
          let scheduleObj =
            document.getElementById("ejs-schedule").ej2_instances[0];
          const myDiv = document.querySelector(".e-content-wrap");

          if (this.hasVerticalScroll(myDiv) && !refreshLayout) {
            refreshLayout = true;
            scheduleObj.refreshLayout();
          }

          if (refreshLayout) args.element.classList.add("set-width");
        }
      }, 1 * args.data.Index);

      setTimeout(() => {
        // args.element.style.width = "100%";
      }, 100 * args.data.Index);

      if (args.data.IsAllDay && this.currentView === "Day") {
        args.element.draggable = true;
        args.element.addEventListener("dragstart", (e) =>
          this.onDragStart({ e, data: args.data }, args.data)
        );
        args.element.addEventListener("dragover", this.onDragOver);
        args.element.addEventListener("drop", (e) => this.onDrop(e, args.data));
      }
    },
    hasVerticalScroll(element) {
      return element.scrollHeight > element.clientHeight;
    },
    onDragOver(e) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    },

    /**
     * on drag stop event - this function get triggers in day view
     * vertical drag & drop
     */
    onDrop(e, targetEventData) {
      e.preventDefault();

      if (this.SelectedScheduleTab === 0)
        this.reOrderWorkOrder({
          ProcessCode: this.ProcessCode,
          OldScheduledDate: new Date(targetEventData.startTime).toSaveDate(),
          NewScheduledDate: new Date(targetEventData.endTime).toSaveDate(),
          WorkOrderCode: draggedEvent.WorkOrderCode,
          NewPosition: targetEventData.Index + 1,
        });
      else {
        this.reOrderLinkSoLines({
          ProcessCode: this.ProcessCode,
          SalesOrderCode: draggedEvent.SalesOrderCode,
          SalesOrderLine: draggedEvent.SalesOrderLine,
          OldScheduledDate: new Date(draggedEvent.PackDate).toSaveDate(),
          NewScheduledDate: new Date(targetEventData.startTime).toSaveDate(),
          WorkOrderCode: draggedEvent.WorkOrderCode,
          NewPosition: targetEventData.Index + 1,
        });
      }
    },

    async reOrderLinkSoLines(payload) {
      this.showLoading(true);
      await ProductionCalendarLinkedSOService.reOrderLinkedSalesOrder(payload)
        .then(() => {
          this.showLoading(false);
          this.onWorkOrderSave();
        })
        .catch((err) => {
          this.showLoading(false);
          this.displayServerError(err);
        });
    },
    async reOrderWorkOrder(payload) {
      this.showLoading(true);
      await WorkOrderService.reOrderWorkOrder(payload)
        .then(() => {
          this.showLoading(false);
          this.onWorkOrderSave();
        })
        .catch((err) => {
          this.showLoading(false);
          this.displayServerError(err);
        });
    },
    onNavigating(e) {
      if (e.action === "view") {
        this.currentView = e.currentView;
      }
      refreshLayout = false;
    },

    /**
     * add new WO event
     */
    addNewWorkOrder() {
      let scheduleObj =
        document.getElementById("ejs-schedule").ej2_instances[0];

      let cellData = {
        data: {
          startTime: new Date(),
        },
        type: "Editor",
      };
      scheduleObj.popupOpen(cellData, "Editor");
    },

    async showEventCreateOrEditModal(args, eventData) {
      args.cancel = true;
      if (eventData) {
        if (this.getPermissionToCreate === false) args.cancel = true;
        else {
          this.dataModel = {
            StartDate: eventData.startTime,
          };

          this.popupConfig.calendarDate = eventData.startTime;
          this.popupConfig.isPopupShow = true;
        }
      }
    },
    /**
     * Hide A popup
     */
    onPopupHide() {
      this.popupConfig.isPopupShow = false;
    },

    onWorkOrderSave() {
      this.onPopupHide();
      this.showLoading(true);
      let scheduleObj =
        document.getElementById("ejs-schedule").ej2_instances[0];

      if (scheduleObj) {
        scheduleObj.refresh();
        this.$nextTick(() => {
          setTimeout(() => {
            this.showLoading(false);
          }, 500);
        });
      } else this.showLoading(false);
    },
  },
  computed: {
    getBranchTabs() {
      return this.branchTabs;
    },
    /**
     * Return user permission for the feature
     */
    getPermissionToCreate() {
      return FeatureStorage.checkPermission("WorkOrder", "create");
    },
    /**
     * Return event settings
     */
    getEventSettings() {
      return {
        dataSource: this.getDataManager,
        fields: {
          id: "Id",
          subject: { name: "subject" },
          description: { name: "Purpose" },
          startTime: { name: "startTime" },
          endTime: { name: "endTime" },
        },
        enableTooltip: true,
        // template: "eventTemplate",
        tooltipTemplate: "tooltipTemplate",
        // enableMaxHeight: true,
        // sortComparer: (args) => {
        //   args.sort((a, b) => a.Sequence - b.Sequence);

        //   return args;
        // },
      };
    },

    /**
     * returns the datasource manager for the scheduler
     */
    getDataManager() {
      if (this.filterValues == undefined || this.ProcessCode == undefined)
        return;

      let scheduleURL =
        this.SelectedScheduleTab === 0
          ? "ProductionScheduleWorkOrder"
          : "ProductionScheduleWorkOrderToSalesOrderLine";

      let filters =
        this.SelectedScheduleTab === 0
          ? `(ProcessCode eq '${this.ProcessCode}') and (BranchCode eq '${this.BranchCode}')&orderby=Sequence asc`
          : `(ProcessCode eq '${this.ProcessCode}') and (BranchCode eq '${this.BranchCode}')&orderby=WorkOrderSequence asc,Position asc`;

      let dataManager = new DataManager({
        url:
          appConfig.VUE_APP_ROOT_API +
          `/api/${scheduleURL}?${
            this.filterValues
              ? `&filter=${this.filterValues} and ${filters}`
              : `&filter=${filters}`
          }`,
        adaptor:
          this.SelectedScheduleTab === 0
            ? new WorkOrderAdaptor()
            : new LinkSOAdaptor(),
        crossDomain: true,
      });

      dataManager.adaptor.beforeSend = function (dm, request) {
        request.setRequestHeader("x-tenant-id", TokenStorage.getTenant());
        request.setRequestHeader(
          "Authorization",
          `Bearer ${TokenStorage.getToken()}`
        );
      };

      return dataManager;
    },

    /**
     * Retun filter panel form items
     */
    filterFormItems() {
      return [
        {
          screenField: "Code",
          filterLabel: "Work Order",
          filterDataField: "WorkOrderCode",
          //   dataType: "Integer",
          type: "Integer",
        },
        {
          screenField: "Product",
          filterDataField: "ProductCode",
        },
        {
          screenField: "BOM",
          filterDataField: "BomCode",
        },
        {
          screenField: "Status",
          // filterDefaultValue: ["Active", "In Production", "Completed"],
          filterDataField: "FriendlyStatus",
          dataSourceConfig: {
            data: [
              { key: "Active", value: "Active" },
              { key: "Cancelled", value: "Cancelled" },
              { key: "Completed", value: "Completed" },
              { key: "Draft", value: "Draft" },
              { key: "In Production", value: "In Production" },
            ],
          },
        },
      ];
    },

    /**
     * return tab value
     */
    getProcessTabs() {
      return this.processTabs;
    },

    /**
     * return selected tab index
     */
    selectedTabIndex() {
      return this.selectedIndex;
    },
  },
};
</script>

<style lang="scss">
.production-calendar {
  // .event-template {
  //   .description {
  //     text-wrap: wrap;
  //   }
  // }

  // .e-schedule .e-vertical-view.e-timescale-disable .e-appointment {
  //   height: auto !important;
  // }

  .e-schedule .e-month-view .e-current-date .e-date-header,
  .e-calendar .e-content td.e-selected span.e-day,
  .e-bigger.e-small .e-calendar .e-content td.e-selected span.e-day {
    background-color: #03a9f4 !important;
    border: 1px solid #03a9f4;
  }

  .e-btn.e-flat.e-primary,
  .e-css.e-btn.e-flat.e-primary,
  .e-active-view .e-tbar-btn-text,
  .e-current-day {
    color: #03a9f4 !important;
  }

  .e-schedule .e-month-view .e-appointment,
  .e-schedule .e-vertical-view.e-timescale-disable .e-appointment {
    // background-color: #03a9f4 !important;

    background-color: unset;
    margin-bottom: 10px;

    .e-appointment-details {
      padding: unset;
      width: inherit;

      > div {
        width: 100%;
      }
    }
  }

  .e-schedule .e-month-view .e-appointment {
    height: 40px !important;

    .e-appointment-details {
      line-height: unset !important;
    }
  }

  .active {
    background: #62d26f;
  }

  .inproduction {
    background-color: #ffa500;
  }

  .completed {
    background-color: #4186e0;
  }

  .cancelled {
    background-color: #e8384f;
  }

  .e-more-popup-wrapper.e-popup.e-popup-open {
    .e-appointment {
      background: unset !important;
      line-height: unset !important;
    }
  }

  .e-content-wrap {
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    box-sizing: border-box;
    .e-appointment-wrapper {
      width: 100%;
      .e-appointment {
        // width: 100% !important;
      }
      .e-appointment.set-width {
        padding-right: 10px;
      }
    }
  }
}
</style>
